import { useParams, Navigate } from "react-router-dom";
import PageHead from "@/components/PageHead";
import { getCity, getRegion, citiesIn, LOCAL_FAQ } from "@/data/locations";
import { INDUSTRIES } from "@/data/industries";
import {
  BRAND_URL, Breadcrumbs, LocalHero, TextBand, LinkGrid, RelatedWork, FaqList, CtaBand, breadcrumbLd, faqLd,
} from "@/components/LocalSections";

const SERVICES = [
  "Custom website design & development",
  "Website redesigns that keep your rankings",
  "Local SEO & Google Business Profile",
  "WCAG 2.2 / ADA-compliant accessibility",
  "E-commerce & online booking",
  "Lead-qualifying forms & AI automation",
];

const industryLinks = INDUSTRIES.map((i) => ({ to: `/industries/${i.slug}`, label: i.short }));

function serviceLd({ name, url, description, areaServed }) {
  return {
    "@type": "ProfessionalService",
    name: "Redkey Web Design",
    url: BRAND_URL,
    logo: `${BRAND_URL}/brand/redkey-logo.png`,
    email: "hello@redkeyweb.design",
    description,
    areaServed,
    makesOffer: { "@type": "Offer", itemOffered: { "@type": "Service", name, url } },
  };
}

function RegionPage({ region }) {
  const path = `/web-design/${region.slug}`;
  const cities = citiesIn(region.slug);
  const crumbs = [{ name: "Home", to: "/" }, { name: "Areas we serve", to: "/locations" }, { name: region.name, to: path }];
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      breadcrumbLd(crumbs),
      serviceLd({
        name: `${region.name} Web Design`, url: `${BRAND_URL}${path}`, description: region.metaDescription,
        areaServed: [{ "@type": "AdministrativeArea", name: `${region.name}, CA` }, ...cities.map((c) => ({ "@type": "City", name: `${c.name}, CA` }))],
      }),
      faqLd(LOCAL_FAQ),
    ],
  };
  const prefill = { location: region.name };
  return (
    <div data-testid={`region-page-${region.slug}`}>
      <PageHead title={region.metaTitle} description={region.metaDescription} canonical={`${BRAND_URL}${path}`} jsonLd={jsonLd} />
      <Breadcrumbs items={crumbs} />
      <LocalHero eyebrow={`§ ${region.counties}`} title={`${region.name} Web Design`} line={region.headline} intro={region.intro} prefill={prefill} testId="region" />
      <TextBand eyebrow="§ The market" title={`What it takes to win online in the ${region.short}`} alt>
        <p>{region.market}</p>
        <p>{region.proof}</p>
      </TextBand>
      <TextBand eyebrow="§ Services" title="What we build">
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
          {SERVICES.map((s) => <li key={s} className="text-ink">— {s}</li>)}
        </ul>
      </TextBand>
      <LinkGrid eyebrow="§ Cities" title={`${region.name} cities we serve`} testId="region-cities" dark
        links={cities.map((c) => ({ to: `/web-design/${c.slug}`, label: c.name }))} />
      <RelatedWork ids={region.relatedWorkIds} testId="region-work" />
      <LinkGrid eyebrow="§ Industries" title="Websites built for your industry" testId="region-industries" links={industryLinks} />
      <FaqList faq={LOCAL_FAQ} testId="region-faq" />
      <CtaBand text={`Ready for a website that wins in the ${region.name}?`} prefill={prefill} testId="region" />
    </div>
  );
}

function CityPage({ city }) {
  const region = getRegion(city.region);
  const path = `/web-design/${city.slug}`;
  const crumbs = [
    { name: "Home", to: "/" }, { name: "Areas we serve", to: "/locations" },
    { name: region.name, to: `/web-design/${region.slug}` }, { name: city.name, to: path },
  ];
  const faq = [...city.faq, ...LOCAL_FAQ];
  const title = `${city.name} Web Design Company | Custom Websites & SEO — Redkey`;
  const description = `Custom web design, local SEO, and ADA-compliant websites for ${city.name}, CA businesses. Hand-coded, fast, and built to bring qualified leads across ${city.county}.`;
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      breadcrumbLd(crumbs),
      serviceLd({
        name: `Web Design in ${city.name}, CA`, url: `${BRAND_URL}${path}`, description,
        areaServed: [{ "@type": "City", name: `${city.name}, CA` }, ...city.nearby.map((n) => ({ "@type": "Place", name: `${n}, CA` }))],
      }),
      faqLd(faq),
    ],
  };
  const prefill = { location: region.name };
  const siblings = citiesIn(region.slug).filter((c) => c.slug !== city.slug);
  return (
    <div data-testid={`city-page-${city.slug}`}>
      <PageHead title={title} description={description} canonical={`${BRAND_URL}${path}`} jsonLd={jsonLd} />
      <Breadcrumbs items={crumbs} />
      <LocalHero eyebrow={`§ ${city.name}, CA · ${city.county}`} title={`Web Design in ${city.name}, CA`} line={city.headline} intro={city.intro} prefill={prefill} testId="city" />
      <TextBand eyebrow="§ Local" title={`Why ${city.name} businesses work with Redkey`} alt>
        <p>{city.local}</p>
        <p>Every site is hand-coded — no page builders — so it loads fast, meets WCAG 2.2 AA, and gives Google clean, structured pages to rank. You work directly with the senior developer building your site.</p>
      </TextBand>
      <TextBand eyebrow="§ Services" title={`Services for ${city.name} businesses`}>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
          {SERVICES.map((s) => <li key={s} className="text-ink">— {s}</li>)}
        </ul>
        <p className="text-base">Also serving nearby: {city.nearby.join(", ")}.</p>
      </TextBand>
      <RelatedWork ids={city.relatedWorkIds} testId="city-work" />
      <LinkGrid eyebrow="§ Industries" title={`${city.name} industries we build for`} testId="city-industries" links={industryLinks} />
      <FaqList faq={faq} testId="city-faq" />
      <LinkGrid eyebrow={`§ More of the ${region.short}`} title={`Other ${region.name} cities`} testId="city-siblings" dark
        links={[{ to: `/web-design/${region.slug}`, label: `All ${region.name}` }, ...siblings.map((c) => ({ to: `/web-design/${c.slug}`, label: c.name }))]} />
      <CtaBand text={`Let's build a website that wins ${city.name}.`} prefill={prefill} testId="city" />
    </div>
  );
}

export default function LocationPage() {
  const { slug } = useParams();
  const region = getRegion(slug);
  if (region) return <RegionPage region={region} />;
  const city = getCity(slug);
  if (city) return <CityPage city={city} />;
  return <Navigate to="/locations" replace />;
}
